import { inject, Injectable, Provider } from '@angular/core';
import { AlertController, ModalController, NavController, ToastController } from '@ionic/angular/standalone';
import * as Sentry from '@sentry/angular';
import { provideSession, SessionHooks } from '@movilidad/app-core';
import { DriverStore } from '@shared/stores/driver.store';
import { QueryClient } from '@tanstack/angular-query-experimental';

import { EventService } from './event.service';
import { LoadingService } from './loading.service';
import { PushRegistrationService } from './push-registration.service';
import { MOVILIDAD_SESSION } from './session.config';

/**
 * Lo que Movilidad hace alrededor del logout de `SessionService` (TEC-293). Es el cuerpo del
 * `AuthService.logout()` de antes, partido en los puntos donde la lib hace su parte; el orden
 * completo está en `SessionHooks` y lo fija el spec "orden de efectos" de `auth.service.spec.ts`.
 */
@Injectable({ providedIn: 'root' })
export class MovilidadSessionHooks implements SessionHooks {
  readonly #pushRegistration = inject(PushRegistrationService);
  readonly #queryClient = inject(QueryClient);
  readonly #driverStore = inject(DriverStore);
  readonly #loadingService = inject(LoadingService);
  readonly #modalCtrl = inject(ModalController);
  readonly #alertCtrl = inject(AlertController);
  readonly #toastCtrl = inject(ToastController);
  readonly #navCtrl = inject(NavController);
  readonly #events = inject(EventService);

  async beforeLogout(): Promise<void> {
    Sentry.setUser(null);

    // Unlink device from user so push notifications stop arriving. Before the
    // stored credentials go away: the API call still needs the session token.
    await this.#pushRegistration.disable();
  }

  async onLogout(): Promise<void> {
    await this.#queryClient.cancelQueries();
    this.#queryClient.resetQueries();
    this.#driverStore.reset();
  }

  async afterLogout(): Promise<void> {
    await Promise.allSettled([
      this.#loadingService.dismiss(),
      this.#modalCtrl.getTop().then((m) => m?.dismiss()),
      this.#alertCtrl.getTop().then((a) => a?.dismiss()),
      this.#toastCtrl.getTop().then((t) => t?.dismiss()),
    ]);

    this.#navCtrl.navigateRoot(['/']);
  }

  /** Por el bus de eventos, como siempre: `AppComponent` lo escucha y llama a `AuthService.logout()`. */
  onUnauthorized(): void {
    this.#events.publish('user:logout');
  }
}

/** Para `main.ts` (y los specs que necesitan la sesión cableada igual que el app). */
export function provideMovilidadSession(): Provider[] {
  return provideSession(MOVILIDAD_SESSION, MovilidadSessionHooks);
}
